import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  DollarSign, Ticket, ShoppingBag, Clock, Trophy, LogOut, Download, Search,
  RefreshCw, Sparkles, Loader2, Car, Plane, Snowflake, RotateCcw,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { EthiopianCross } from "@/components/EthiopianCross";
import { api, API, formatApiError } from "@/lib/api";

const PRIZE_META = {
  1: { title: "2026 Toyota Highlander", value: "$50,000", Icon: Car, color: "#9E1B1B" },
  2: { title: "Flight to Ethiopia", value: "$2,000", Icon: Plane, color: "#C68E17" },
  3: { title: "Deep Freezer", value: "$1,000", Icon: Snowflake, color: "#4A4A4A" },
};

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm" data-testid={`stat-${label.replace(/\s/g, "-").toLowerCase()}`}>
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">{label}</span>
      <Icon className="w-5 h-5" style={{ color: accent }} />
    </div>
    <div className="mt-2 font-mono text-2xl font-bold" style={{ color: accent }}>{value}</div>
  </div>
);

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("sales");
  const [stats, setStats] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [winners, setWinners] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [drawing, setDrawing] = useState(null);

  const logout = () => { localStorage.removeItem("raffle_admin_token"); nav("/admin/login"); };

  const loadStats = useCallback(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch((e) => {
      if (e.response?.status === 401) { logout(); }
    });
  }, []);

  const loadWinners = useCallback(() => {
    api.get("/admin/winners").then(({ data }) => setWinners(data.winners)).catch(() => {});
  }, []);

  const loadPurchases = useCallback(() => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (statusFilter !== "all") params.status = statusFilter;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    api.get("/admin/purchases", { params })
      .then(({ data }) => setPurchases(data.purchases))
      .catch((e) => { if (e.response?.status === 401) logout(); })
      .finally(() => setLoading(false));
  }, [search, statusFilter, startDate, endDate]);

  useEffect(() => {
    if (!localStorage.getItem("raffle_admin_token")) { nav("/admin/login"); return; }
    loadStats(); loadWinners(); loadPurchases();
  }, []);

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem("raffle_admin_token");
      const res = await fetch(`${API}/admin/export`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "raffle-sales-export.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV exported.");
    } catch { toast.error("Export failed."); }
  };

  const draw = async (rank) => {
    setDrawing(rank);
    try {
      const { data } = await api.post("/admin/draw", { prize_rank: rank });
      toast.success(`Prize #${rank} winner: ${data.ticket_number}`);
      loadWinners();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Draw failed.");
    } finally { setDrawing(null); }
  };

  const resetDraws = async () => {
    try {
      await api.delete("/admin/winners");
      toast.success("Draws reset.");
      loadWinners();
    } catch { toast.error("Reset failed."); }
  };

  const resetRaffle = async () => {
    try {
      const { data } = await api.post("/admin/reset-raffle");
      const d = data.deleted;
      toast.success(`All data cleared (${d.purchases} orders, ${d.tickets} tickets). Next ticket: ${data.next_ticket}.`);
      loadStats(); loadWinners(); loadPurchases();
    } catch { toast.error("Reset failed."); }
  };

  const winnerFor = (rank) => winners.find((w) => w.prize_rank === rank);

  return (
    <div className="min-h-screen bg-[#F3EFE6]">
      <header className="bg-[#1A1A1A] text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <EthiopianCross className="w-9 h-9 bg-white rounded-full p-0.5" />
            <span className="font-heading text-lg font-bold">Raffle Admin</span>
          </div>
          <Button onClick={logout} variant="ghost" className="text-white/80 hover:text-white hover:bg-white/10" data-testid="admin-logout">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <StatCard icon={DollarSign} label="Total Revenue" value={stats ? `$${stats.total_revenue.toLocaleString()}` : "—"} accent="#2D6A4F" />
          <StatCard icon={Ticket} label="Tickets Sold" value={stats ? stats.tickets_sold.toLocaleString() : "—"} accent="#9E1B1B" />
          <StatCard icon={ShoppingBag} label="Paid Orders" value={stats ? stats.total_orders : "—"} accent="#C68E17" />
          <StatCard icon={Clock} label="Pending" value={stats ? stats.pending_orders : "—"} accent="#F4A261" />
          <StatCard icon={Trophy} label="Winners Drawn" value={stats ? stats.winners_drawn : "—"} accent="#9E1B1B" />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-300">
          {[["sales", "Ticket Sales"], ["draw", "Winner Drawing"]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
              className={`px-5 py-2.5 font-semibold text-sm transition-colors border-b-2 -mb-px ${tab === k ? "border-[#9E1B1B] text-[#9E1B1B]" : "border-transparent text-gray-500 hover:text-[#1A1A1A]"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "sales" && (
          <div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[220px]">
                <label className="text-xs text-gray-500 font-semibold">Search (name, email, phone, ticket #)</label>
                <div className="relative mt-1">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" placeholder="Search buyers…" data-testid="admin-search" />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 font-semibold">Status</label>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="admin-status-filter"
                  className="mt-1 flex h-9 rounded-md border border-input bg-white px-3 text-sm">
                  <option value="all">All</option>
                  <option value="paid">Paid</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 font-semibold">From</label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="mt-1" data-testid="admin-start-date" />
              </div>
              <div>
                <label className="text-xs text-gray-500 font-semibold">To</label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="mt-1" data-testid="admin-end-date" />
              </div>
              <Button onClick={loadPurchases} className="bg-[#9E1B1B] hover:bg-[#7A1414] text-white" data-testid="admin-apply-filters">
                <RefreshCw className="w-4 h-4 mr-2" /> Apply
              </Button>
              <Button onClick={exportCsv} variant="outline" className="border-[#2D6A4F] text-[#2D6A4F] hover:bg-[#2D6A4F] hover:text-white" data-testid="admin-export-csv">
                <Download className="w-4 h-4 mr-2" /> Export CSV
              </Button>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-purchases-table">
                  <thead className="bg-[#1A1A1A] text-white text-left">
                    <tr>
                      {["Date", "Name", "Contact", "Location", "Qty", "Amount", "Status", "Ticket #s"].map((h) => (
                        <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading && <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></td></tr>}
                    {!loading && purchases.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400">No records found.</td></tr>}
                    {!loading && purchases.map((p) => (
                      <tr key={p.id} className="border-b border-gray-100 hover:bg-[#FAF9F6]" data-testid={`purchase-row-${p.id}`}>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-500">{new Date(p.created_at).toLocaleDateString()}</td>
                        <td className="px-4 py-3 font-medium">{p.full_name}</td>
                        <td className="px-4 py-3 text-gray-600"><div>{p.email}</div><div className="text-xs">{p.phone}</div></td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{p.city}, {p.state}</td>
                        <td className="px-4 py-3 font-mono">{p.quantity}</td>
                        <td className="px-4 py-3 font-mono">${p.amount.toFixed(2)}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${p.payment_status === "paid" ? "bg-[#2D6A4F]/10 text-[#2D6A4F]" : "bg-[#F4A261]/15 text-[#B45309]"}`}>
                            {p.payment_status}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-[#9E1B1B] max-w-[180px]">{(p.ticket_numbers || []).join(", ") || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-8 border border-red-200 rounded-lg p-5 bg-red-50/60" data-testid="danger-zone">
              <h3 className="font-semibold text-red-700">Danger Zone</h3>
              <p className="text-sm text-red-700/80 mt-1 max-w-2xl">Permanently delete <strong>all</strong> sales, tickets, transactions and winners, and reset ticket numbering back to 2001. Use this once to clear test data before your real raffle begins. This cannot be undone.</p>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button className="mt-3 bg-red-600 hover:bg-red-700 text-white" data-testid="reset-raffle-btn">Reset All Raffle Data</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete ALL raffle data?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This permanently deletes every purchase, ticket, transaction, and winner, and resets ticket numbering to <strong>2001</strong>. This action cannot be undone. Only do this to clear test data before real sales begin.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="cancel-reset-raffle">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={resetRaffle} className="bg-red-600 hover:bg-red-700" data-testid="confirm-reset-raffle">Yes, delete everything</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        )}

        {tab === "draw" && (
          <div>
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 flex items-center justify-between flex-wrap gap-4">
              <div>
                <h2 className="font-heading text-2xl font-bold flex items-center gap-2"><Sparkles className="w-6 h-6 text-[#C68E17]" /> Winner Drawing Tool</h2>
                <p className="text-sm text-gray-500 mt-1">Randomly selects a winner from all paid tickets. Each ticket can win only once.</p>
              </div>
              <Button onClick={resetDraws} variant="outline" className="border-gray-300 text-gray-600" data-testid="reset-draws">
                <RotateCcw className="w-4 h-4 mr-2" /> Reset Draws
              </Button>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {[1, 2, 3].map((rank) => {
                const meta = PRIZE_META[rank];
                const w = winnerFor(rank);
                return (
                  <div key={rank} className="bg-white border border-gray-200 rounded-lg p-6 text-center" data-testid={`draw-card-${rank}`}>
                    <meta.Icon className="w-10 h-10 mx-auto" style={{ color: meta.color }} />
                    <div className="text-xs uppercase tracking-widest text-gray-400 mt-3 font-semibold">Prize #{rank}</div>
                    <h3 className="font-heading text-xl font-bold mt-1">{meta.title}</h3>
                    <p className="font-mono text-sm text-gray-500">{meta.value}</p>
                    {w ? (
                      <div className="mt-5 bg-[#F3EFE6] rounded-lg p-4" data-testid={`winner-${rank}`}>
                        <div className="ticket-number text-3xl font-bold text-[#9E1B1B]">{w.ticket_number}</div>
                        <p className="text-sm font-medium mt-2">{w.buyer_name}</p>
                        <p className="text-xs text-gray-500">{w.buyer_email}</p>
                        <p className="text-[10px] text-gray-400 mt-2">Drawn {new Date(w.drawn_at).toLocaleString()}</p>
                      </div>
                    ) : (
                      <Button onClick={() => draw(rank)} disabled={drawing !== null}
                        className="mt-5 w-full text-white font-semibold" style={{ backgroundColor: meta.color }} data-testid={`draw-btn-${rank}`}>
                        {drawing === rank ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-2" /> Draw Winner</>}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
